import { ref, reactive, computed } from 'vue'
import { useChatSocket } from './useChatSocket'
import { useChatAudio } from './useChatAudio'
import type { ChatMessage } from './useGlobalChat'

/**
 * DM thread state: list + per-thread message cache + open/close + send.
 *
 * REST is authoritative (history, send). WS provides live `message.new`
 * pushes (for threads the peer has subscribed to via `subscribe.thread`) and
 * `thread.unread` pushes on the user channel (for the inbox badge).
 *
 * "Active thread" is the one currently visible in ChatThreadView — only one
 * at a time. Switching active thread fires subscribe/unsubscribe frames so
 * the server only fans out messages to peers actually watching.
 */

export interface ThreadRow {
    id: string
    kind: 'dm' | 'global'
    participants: Array<{ id: string; name: string; avatar: string | null; role: string }>
    otherUser: { id: string; name: string; avatar: string | null; role: string } | null
    lastMessageAt: string | null
    lastMessagePreview: string
    lastMessageBy: string | null
    unread: number
    createdAt: string
    updatedAt: string
}

const threads = ref<ThreadRow[]>([])
const threadsLoading = ref(false)
const threadsLoaded = ref(false)

// Role-derived room threads (label / staff) live HERE, not in `threads`.
// Reasons they can't go in the DM list:
//   - `loadThreads()` overwrites `threads` wholesale with the dm-only inbox,
//     which would wipe them (and blank ChatThreadView's header), and
//   - ChatThreadRow renders anything non-'group' as a DM (a random member's
//     name/avatar), so they'd show as broken rows in the DM list.
// ChatThreadView resolves its header/kind from `threads` OR this map.
const roomThreadsById = reactive(new Map<string, ThreadRow>())

// Per-thread message cache
const messagesByThread = reactive(new Map<string, ChatMessage[]>())
const threadLoading = reactive(new Map<string, boolean>())
const threadHasMore = reactive(new Map<string, boolean>())
const threadSending = reactive(new Map<string, boolean>())

const activeThreadId = ref<string | null>(null)

let wired = false

// Merge a hydrated label/staff room thread into the side map (not the DM
// list). Used by role-derived rooms so ChatThreadView can resolve their
// header/kind without them leaking into the DM inbox.
function upsertRoomThread(row: ThreadRow) {
    if (row?.id) roomThreadsById.set(row.id, row)
}

function upsertThreadInList(row: ThreadRow) {
    const idx = threads.value.findIndex(t => t.id === row.id)
    if (idx >= 0) {
        threads.value[idx] = { ...threads.value[idx], ...row }
    } else {
        threads.value.unshift(row)
    }
    sortThreads()
}

function sortThreads() {
    threads.value.sort((a, b) => {
        const at = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0
        const bt = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0
        return bt - at
    })
}

function mergeMessage(threadId: string, incoming: ChatMessage) {
    const list = messagesByThread.get(threadId) || []
    const idx = list.findIndex(m =>
        m.id === incoming.id ||
        (incoming.clientMsgId && m.clientMsgId === incoming.clientMsgId)
    )
    if (idx >= 0) list[idx] = { ...list[idx], ...incoming }
    else list.push(incoming)
    messagesByThread.set(threadId, list)
}

function wire() {
    if (wired) return
    // Skip on the server — the socket singleton + audio playback only make
    // sense in the browser, and touching them during SSR will throw on
    // `window`/`Audio` access.
    if (!import.meta.client) return
    wired = true
    const { on, send } = useChatSocket()
    const { playMessage } = useChatAudio()
    const { user } = useUserSession()

    on('message.new', (frame) => {
        // Accept every per-thread room this composable drives — DM, group,
        // label and staff. (global + bugs have their own composables.)
        // Without 'label'/'staff' here, live messages in those rooms were
        // dropped and only appeared on reopen/refetch.
        if (!frame?.message) return
        if (!['dm', 'group', 'label', 'staff'].includes(frame.message.kind)) return
        const threadId = frame.thread?.id || frame.message.threadId
        if (!threadId) return

        // Update cached messages if we have them
        if (messagesByThread.has(threadId)) {
            mergeMessage(threadId, frame.message)
        }

        // Patch the thread-list row with the new preview/lastMessage
        const existing = threads.value.find(t => t.id === threadId)
        if (existing) {
            existing.lastMessageAt = frame.message.createdAt
            existing.lastMessagePreview = (frame.message.body || '').slice(0, 160)
            existing.lastMessageBy = frame.message.author?.id || null
            // If this isn't the active thread and not authored by me, bump unread
            // (Authoritative unread comes via thread.unread pushes; this is a UX hint
            //  so the list reorders immediately even if the targeted push is a tick late.)
            sortThreads()
        } else if (frame.message.kind === 'dm' || frame.message.kind === 'group') {
            // Unknown DM/group thread: reload the inbox to pick up a new
            // conversation started by someone else. Scoped to dm/group on
            // purpose — label/staff rooms live in `roomThreadsById`, not the
            // DM list, so they're legitimately absent here; reloading on their
            // messages would be a per-message inbox refetch storm.
            loadThreads().catch(() => { /* noop */ })
        }

        // Audio notification — skip if the message is from the user itself
        // (e.g. echoed from another tab). Debounce-per-sender lives inside
        // playMessage so rapid-fire messages from one person stay quiet.
        const meId = String((user.value as any)?._id || (user.value as any)?.id || '')
        const authorId = String(frame.message.author?.id || '')
        if (authorId && authorId !== meId) {
            playMessage(authorId).catch(() => { /* noop */ })
        }
    })

    on('message.delete', (frame) => {
        const threadId = frame.threadId
        if (!threadId) return
        const list = messagesByThread.get(threadId)
        if (!list) return
        const msg = list.find(m => m.id === frame.messageId)
        if (msg) {
            msg.deletedAt = new Date().toISOString()
            msg.body = ''
            msg.attachments = []
            msg.links = []
        }
    })

    on('message.linkPreview', (frame) => {
        if (!frame?.threadId) return
        const list = messagesByThread.get(frame.threadId)
        const msg = list?.find(m => m.id === frame.messageId)
        if (!msg) return
        const link = msg.links.find((l: any) => l.url === frame.url)
        if (link) link.preview = frame.preview
    })

    // Edit — server pushes the freshly-serialised message; drop-in replace.
    on('message.updated', (frame) => {
        if (!frame?.threadId || !frame?.message) return
        const list = messagesByThread.get(frame.threadId)
        if (!list) return
        const idx = list.findIndex(m => m.id === frame.message.id)
        if (idx >= 0) Object.assign(list[idx], frame.message)
    })

    // Reactions — incremental {emoji, userId} adds/removes. We re-bucket
    // locally rather than asking the server to re-send the whole message
    // (cheaper on the wire, and the client already knows the viewer id).
    const meIdLocal = () => String((useUserSession().user.value as any)?._id || (useUserSession().user.value as any)?.id || '')
    function applyReactionDelta(frame: any, delta: 1 | -1) {
        if (!frame?.threadId || !frame?.messageId || !frame?.emoji) return
        const list = messagesByThread.get(frame.threadId)
        const msg = list?.find(m => m.id === frame.messageId)
        if (!msg) return
        const reactions = (msg.reactions = msg.reactions || [])
        const mine = String(frame.userId) === meIdLocal()
        const existing = reactions.find((r: any) => r.emoji === frame.emoji)
        if (delta > 0) {
            if (existing) {
                existing.count += 1
                if (mine) existing.mine = true
            } else {
                reactions.push({ emoji: frame.emoji, count: 1, mine })
            }
        } else {
            if (!existing) return
            existing.count = Math.max(0, existing.count - 1)
            if (mine) existing.mine = false
            if (existing.count <= 0) {
                const i = reactions.indexOf(existing)
                if (i >= 0) reactions.splice(i, 1)
            }
        }
    }
    on('message.reaction.added', (frame) => applyReactionDelta(frame, 1))
    on('message.reaction.removed', (frame) => applyReactionDelta(frame, -1))

    // Pinned-message banner update. Server publishes both pin + unpin via
    // the same frame (pinnedMessageId === null = unpinned).
    on('thread.pinUpdated', (frame) => {
        if (typeof frame?.threadId !== 'string') return
        // Resolve from the DM list OR the role-derived room side-map: label +
        // staff rooms live only in `roomThreadsById`, so without this fallback
        // their live pin banner never updated (ChatThreadView reads
        // `thread.pinnedMessageId` off the resolved row).
        const t = threads.value.find(x => x.id === frame.threadId)
            || roomThreadsById.get(frame.threadId)
        if (!t) return
        ;(t as any).pinnedMessageId = frame.pinnedMessageId || null
        ;(t as any).pinnedAt = frame.pinnedAt || null
        ;(t as any).pinnedBy = frame.pinnedBy || null
    })

    on('thread.unread', (frame) => {
        if (typeof frame?.threadId !== 'string') return
        const t = threads.value.find(x => x.id === frame.threadId)
            || roomThreadsById.get(frame.threadId)
        if (t) t.unread = Math.max(0, Number(frame.count) || 0)
    })

    // Group creation — server pushes a pre-hydrated thread row to every
    // participant so their thread list picks it up instantly. Skip the
    // creator's own push if we already inserted it locally (we did — the
    // POST response handled it).
    on('thread.created', (frame) => {
        if (!frame?.thread || typeof frame.thread.id !== 'string') return
        const exists = threads.value.some(t => t.id === frame.thread.id)
        if (!exists) {
            threads.value = [frame.thread, ...threads.value]
            sortThreads()
        }
    })

    // Resync on reconnect
    on('__open', () => {
        if (threadsLoaded.value) loadThreads()
        // Re-send subscribe.thread for the active thread so we get live pushes
        if (activeThreadId.value) {
            send({ type: 'subscribe.thread', threadId: activeThreadId.value })
        }
    })
}

async function loadThreads() {
    threadsLoading.value = true
    try {
        const res = await $fetch<{ threads: ThreadRow[] }>('/api/chat/threads')
        threads.value = res.threads || []
        sortThreads()
        threadsLoaded.value = true
    } catch (err: any) {
        if (err?.statusCode === 404) threadsLoaded.value = true
        else console.error('[chat:threads] load failed', err)
    } finally {
        threadsLoading.value = false
    }
}

async function loadThreadMessages(threadId: string) {
    threadLoading.set(threadId, true)
    try {
        const res = await $fetch<{ messages: ChatMessage[]; hasMore: boolean }>(`/api/chat/threads/${threadId}/messages`)
        // Server returns newest-first → chronological for render
        const chronological = [...res.messages].reverse()
        // Merge with any optimistic sends still pending
        const existing = messagesByThread.get(threadId) || []
        const pendingLocal = existing.filter(m => m.sendState === 'sending' || m.sendState === 'failed')
        const merged = chronological
        for (const p of pendingLocal) {
            if (!merged.some(m => m.id === p.id || (p.clientMsgId && m.clientMsgId === p.clientMsgId))) {
                merged.push(p)
            }
        }
        messagesByThread.set(threadId, merged)
        threadHasMore.set(threadId, res.hasMore)
    } catch (err) {
        console.error('[chat:threads] load messages failed', err)
    } finally {
        threadLoading.set(threadId, false)
    }
}

async function loadOlderMessages(threadId: string) {
    if (!threadHasMore.get(threadId)) return
    const list = messagesByThread.get(threadId) || []
    const oldest = list[0]
    if (!oldest) return
    try {
        const res = await $fetch<{ messages: ChatMessage[]; hasMore: boolean }>(`/api/chat/threads/${threadId}/messages`, {
            params: { before: oldest.createdAt }
        })
        const older = [...res.messages].reverse()
        messagesByThread.set(threadId, [...older, ...list])
        threadHasMore.set(threadId, res.hasMore)
    } catch (err) {
        console.error('[chat:threads] load older failed', err)
    }
}

/** Open a thread: subscribe to its channel, load history if not cached, set active. */
async function openThread(threadId: string): Promise<void> {
    const { send } = useChatSocket()

    // Unsubscribe from previous thread
    if (activeThreadId.value && activeThreadId.value !== threadId) {
        send({ type: 'unsubscribe.thread', threadId: activeThreadId.value })
    }
    activeThreadId.value = threadId

    // Subscribe so we receive message.new frames for this thread
    send({ type: 'subscribe.thread', threadId })

    // Load history if we don't have it
    if (!messagesByThread.has(threadId)) {
        await loadThreadMessages(threadId)
    }

    // Mark read
    try {
        await $fetch(`/api/chat/threads/${threadId}/read`, { method: 'POST' })
        const t = threads.value.find(x => x.id === threadId)
        if (t) t.unread = 0
    } catch (err) {
        console.error('[chat:threads] mark read failed', err)
    }
}

function closeActiveThread() {
    const { send } = useChatSocket()
    if (activeThreadId.value) {
        send({ type: 'unsubscribe.thread', threadId: activeThreadId.value })
        activeThreadId.value = null
    }
}

/** Find-or-create a DM with another user. Returns the thread id. */
async function startDM(withUserId: string): Promise<string | null> {
    try {
        const res = await $fetch<{ thread: ThreadRow }>('/api/chat/threads', {
            method: 'POST',
            body: { withUserId }
        })
        upsertThreadInList(res.thread)
        return res.thread.id
    } catch (err) {
        console.error('[chat:threads] start DM failed', err)
        return null
    }
}

function makeClientMsgId(): string {
    return 'cm_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
}

/** Optimistic send to a DM thread. */
async function sendMessage(threadId: string, text: string, currentUser: { id: string; name: string; avatar: string | null; role: string }, attachments: any[] = [], replyToId: string | null = null) {
    const trimmed = text.trim()
    if (!trimmed && attachments.length === 0) return

    const clientMsgId = makeClientMsgId()
    const optimistic: ChatMessage = {
        id: clientMsgId,
        clientMsgId,
        threadId,
        kind: 'dm',
        author: currentUser,
        body: trimmed,
        attachments,
        links: [],
        deletedAt: null,
        editedAt: null,
        createdAt: new Date().toISOString(),
        sendState: 'sending'
    }
    const list = messagesByThread.get(threadId) || []
    list.push(optimistic)
    messagesByThread.set(threadId, list)
    threadSending.set(threadId, true)

    try {
        const res = await $fetch<{ ok: boolean; message: ChatMessage }>(`/api/chat/threads/${threadId}/messages`, {
            method: 'POST',
            body: { body: trimmed, attachments, clientMsgId, replyTo: replyToId }
        })
        const idx = list.findIndex(m => m.clientMsgId === clientMsgId)
        if (idx >= 0) list[idx] = { ...res.message, clientMsgId, sendState: 'sent' }
        const t = threads.value.find(x => x.id === threadId)
        if (t) {
            t.lastMessageAt = res.message.createdAt
            t.lastMessagePreview = (res.message.body || (attachments.length > 0 ? '📎 Image' : '')).slice(0, 160)
            t.lastMessageBy = currentUser.id
            sortThreads()
        }
    } catch (err: any) {
        const msg = list.find(m => m.clientMsgId === clientMsgId)
        if (msg) {
            msg.sendState = 'failed'
            msg.sendError = err?.data?.statusMessage || err?.statusMessage || err?.message || 'send_failed'
        }
    } finally {
        threadSending.set(threadId, false)
    }
}

async function retrySend(threadId: string, clientMsgId: string, currentUser: { id: string; name: string; avatar: string | null; role: string }) {
    const list = messagesByThread.get(threadId) || []
    const msg = list.find(m => m.clientMsgId === clientMsgId)
    if (!msg || msg.sendState !== 'failed') return
    const body = msg.body
    const attachments = msg.attachments
    messagesByThread.set(threadId, list.filter(m => m.clientMsgId !== clientMsgId))
    await sendMessage(threadId, body, currentUser, attachments)
}

export function useChatThreads() {
    wire()

    const threadMessages = (threadId: string) => computed<ChatMessage[]>(() => messagesByThread.get(threadId) || [])
    const isThreadLoading = (threadId: string) => computed(() => !!threadLoading.get(threadId))
    const threadHasMoreFor = (threadId: string) => computed(() => !!threadHasMore.get(threadId))
    const isThreadSending = (threadId: string) => computed(() => !!threadSending.get(threadId))

    return {
        threads: computed(() => threads.value),
        threadsLoading: computed(() => threadsLoading.value),
        threadsLoaded: computed(() => threadsLoaded.value),
        activeThreadId: computed(() => activeThreadId.value),
        loadThreads,
        startDM,
        openThread,
        closeActiveThread,
        // Role-derived room (label/staff) metadata for ChatThreadView. The
        // map is kept out of the DM list on purpose (see roomThreadsById).
        upsertRoomThread,
        roomThreadsById,
        loadOlderMessages,
        sendMessage,
        retrySend,
        threadMessages,
        isThreadLoading,
        threadHasMoreFor,
        isThreadSending,
    }
}
